import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import NotificationService from "../../api/services/notification.service";
import { ChatNotification } from "../../api/types/notification.types";
import { useNotifications } from "../../context/NotificationContext";
import styles from "./Notifications.module.scss";

const Notifications = () => {
  const { t } = useTranslation("translation");
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const { refreshCount, resetCount } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await NotificationService.getNotifications();
      setNotifications(res.data?.notifications || []);
    } catch (e) {
      console.error("Failed to fetch notifications", e);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: ChatNotification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await NotificationService.markNotificationRead(notification.id);
        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n,
          ),
        );
        refreshCount();
      } catch (e) {
        console.error("Failed to mark notification as read", e);
      }
    }
    // Navigate to messages page with the session
    navigate(`/dashboard/messages?session_id=${notification.session_id}`);
  };

  const handleMarkAllRead = async () => {
    try {
      await NotificationService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      resetCount();
    } catch (e) {
      console.error("Failed to mark all notifications as read", e);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("notifications.just_now") as string;
    if (diffMins < 60)
      return `${diffMins}${t("notifications.m") as string} ${t("notifications.ago") as string}`;
    if (diffHours < 24)
      return `${diffHours}${t("notifications.h") as string} ${t("notifications.ago") as string}`;
    if (diffDays < 7)
      return `${diffDays}${t("notifications.d") as string} ${t("notifications.ago") as string}`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className={styles.notifications}>
      <TitleUpdater title={t("notifications.title") as string} />
      <div className={styles.header}>
        <h2>{t("notifications.title") as string}</h2>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className={styles.markAllBtn}>
            {t("notifications.mark_all_read") as string}
          </button>
        )}
      </div>

      {loading ? (
        <div className={styles.loading}>
          {t("notifications.loading") as string}
        </div>
      ) : notifications.length === 0 ? (
        <div className={styles.empty}>
          <p>{t("notifications.no_notifications") as string}</p>
        </div>
      ) : (
        <div className={styles.list}>
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${styles.item} ${
                !notification.is_read ? styles.unread : ""
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className={styles.avatar}>
                {notification.store_logo_url ? (
                  <img src={notification.store_logo_url} alt="Store logo" />
                ) : (
                  <div className={styles.defaultAvatar}>
                    {notification.store_name?.charAt(0) || "S"}
                  </div>
                )}
              </div>
              <div className={styles.content}>
                <div className={styles.title}>
                  <strong>
                    {notification.store_name ||
                      (t("notifications.store_fallback") as string)}
                  </strong>{" "}
                  {t("notifications.sent_message") as string}
                </div>
                <div className={styles.message}>
                  {notification.content ||
                    (t("notifications.new_message") as string)}
                </div>
                <div className={styles.time}>
                  {formatTime(notification.created)}
                </div>
              </div>
              {!notification.is_read && <div className={styles.dot}></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
